import { ArrowRightIcon, GraduationCapIcon, InfoIcon } from "lucide-react";
import { Tooltip } from "@component/tooltip";
import { useEffect, useState } from "react";
import { InterviewItem, PracticeInterviewResponseItem } from "@type/interview";
import { getCompletedPracticeInterviews, getOurteamInterviews } from "@service/interview";
import { catchErrorLog } from "@util/sentry";
import { Link } from "react-router-dom";
import InterviewItemCard from "@component/interview-item-card";
import AuthLayout from "@container/layout/auth";

export default function CandidateInterviewPage() {
    const [interviews, setInterviews] = useState<InterviewItem[]>([]);

    const [practiceInterviews, setPracticeInterviews] = useState<InterviewItem[]>([]);
    const [isLoadingPractice, setIsLoadingPractice] = useState<boolean>(true);
    const [isLoadingOurteamInterview, setIsLoadingOurteamInterview] = useState<boolean>(true);

    const fetchPracticeInterviews = async () => {
        try {
            setIsLoadingPractice(true);
            const response = await getCompletedPracticeInterviews();

            if (response.data) {
                // Transform the practice interviews to match InterviewItem format
                // @ts-ignore
                const formattedInterviews = response.data.map((interview: PracticeInterviewResponseItem) => {
                    // Parse content to extract the interview name
                    const interviewContent = interview.content as any;

                    return {
                        id: interview.id,
                        alias: interview.type,
                        title: interviewContent.interviewName || "Practice Interview",
                        company_name: "ourteam",
                        is_premium: false,
                        interview_type: "practice",
                        status: interview.status,
                        created_at: interview.created_at,
                        duration: interviewContent.maxDuration || 0, // Default to 0 if not provided
                        link: `/interview/${interview.id}`
                    };
                });

                setPracticeInterviews(formattedInterviews as InterviewItem[]);
            }
        } catch (error) {
            catchErrorLog(`failed to get practice interviews, err: ${error}`);
        } finally {
            setIsLoadingPractice(false);
        }
    };

    const fetchOurteamInterviews = async () => {
        try {
            const response = await getOurteamInterviews();
            if (response.status === 200) {
                const formattedInterviews = response.data.data.map((interview: PracticeInterviewResponseItem) => {
                    // Parse content to extract the interview name
                    const interviewContent = interview.content as any;

                    return {
                        id: interview.id,
                        alias: interview.type,
                        title: interviewContent?.interviewName || interview.title || "Official Interview",
                        company_name: interview.company_name || "ourteam",
                        job_name: interview.job_name,
                        job_id: interview.job_id,
                        is_premium: false,
                        interview_type: "official",
                        status: interview.status,
                        created_at: interview.created_at,
                        duration: interviewContent?.maxDuration || interview.duration || 0, // Default to 0 if not provided
                        link: (interview?.alias) ? interview.link : `/interview/${interview.id}`
                    };
                });
                setInterviews(formattedInterviews as InterviewItem[]);
            }
        } catch (error) {
            catchErrorLog(`failed to get ourteam interviews, err: ${error}`);
        } finally {
            setIsLoadingOurteamInterview(false);
        }
    };

    useEffect(() => {
        fetchPracticeInterviews();
        fetchOurteamInterviews();
    }, []);

    return (
        <AuthLayout>
            <div>
                <div>
                    <h1 className="text-2xl font-bold text-black">
                        My Interviews
                    </h1>
                    <p className="text-gray-600">Prepare through practice sessions and complete real interview opportunities.</p>
                </div>

                {/* Special Interviews Section */}
                <div className="flex items-center justify-between mb-4 mt-8">
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold text-gray-800">
                            Official Interviews
                        </h2>
                        <Tooltip
                            content={
                                <p>You can only attempt interviews published by<br />ourteam or partner companies once, so be sure to<br /> make it count!</p>
                            }
                            side="top"
                            delayDuration={0}
                        >
                            <InfoIcon className="h-4 w-4 text-blue-500 cursor-help" />
                        </Tooltip>
                    </div>
                </div>
                {isLoadingOurteamInterview ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin h-8 w-8 border-4 border-blue-500 rounded-full border-t-transparent"></div>
                    </div>
                ) : interviews.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="mx-auto h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="h-8 w-8 text-gray-400"
                            >
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />

                                <polyline points="14 2 14 8 20 8" />

                                <line x1="16" y1="13" x2="8" y2="13" />

                                <line x1="16" y1="17" x2="8" y2="17" />

                                <line x1="10" y1="9" x2="8" y2="9" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-800 mb-2">
                            No ourteam interview available.
                        </h3>
                        <p className="text-gray-500 max-w-md mx-auto">
                            There is no ourteam interview for you to attempt yet.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4 w-full">
                        {interviews.map((interview, index) => (
                            <InterviewItemCard
                                key={index}
                                interview={interview}
                            />
                        ))}
                    </div>
                )}

                {/* Practice Interviews Section */}
                <div className="flex flex-row items-center justify-between mb-4 mt-12">
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold text-gray-800">
                            Completed Practice Interviews
                        </h2>
                        <Tooltip
                            content={
                                <p>Practice interviews can be reattempted multiple times to build confidence<br /> and interview skills. Your completed interviews and feedback will appear here.</p>
                            }
                            side="top"
                            delayDuration={0}
                        >
                            <InfoIcon className="h-4 w-4 text-blue-500 cursor-help" />
                        </Tooltip>
                    </div>
                    <Link
                        className="flex items-center text-sm font-medium text-blue-500 hover:text-black transition-colors"
                        to={import.meta.env.VITE_MODE_DEMO_PRACTICE_INTERVIEW === "true" ? "/browse" : "/interview/browse"}
                        {...(import.meta.env.VITE_MODE_DEMO_PRACTICE_INTERVIEW !== "true" && { target: "_blank" })}
                    >
                        Browse<span className="sm:block hidden sm:ml-1">Practice Interviews</span>
                        <ArrowRightIcon className="ml-1 h-4 w-4" />
                    </Link>
                </div>

                {isLoadingPractice ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin h-8 w-8 border-4 border-blue-500 rounded-full border-t-transparent"></div>
                    </div>
                ) : practiceInterviews.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="bg-gray-100 p-4 rounded-full mb-4">
                                <GraduationCapIcon className="h-8 w-8 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                No completed practice interviews.
                            </h3>
                            <p className="text-gray-500 max-w-sm">
                                You haven't completed any practice interviews yet.
                            </p>
                            <p className="text-gray-500 max-w-sm mt-2">
                                Start practicing to improve your interview skills and view your
                                feedback here.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4 w-full">
                        {practiceInterviews.map((interview, index) => (
                            <InterviewItemCard
                                key={`practice-${index}`}
                                interview={interview}
                                is_practice
                                hideDropdown
                            />
                        ))}
                    </div>
                )}
            </div>
        </AuthLayout>
    )
}